import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface SettingsRowBaseProps {
  label: string;
  description?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}

interface SettingsRowButtonProps
  extends SettingsRowBaseProps,
    ButtonHTMLAttributes<HTMLButtonElement> {
  href?: never;
}

interface SettingsRowLinkProps extends SettingsRowBaseProps {
  href: string;
  target?: string;
  rel?: string;
  onClick?: never;
}

type SettingsRowProps = SettingsRowButtonProps | SettingsRowLinkProps;

function Content({ label, description, leading, trailing }: SettingsRowBaseProps) {
  return (
    <>
      {leading ? (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(107,78,230,0.08)] text-[var(--primary)]">
          {leading}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="text-[14px] text-[var(--foreground)]">{label}</p>
        {description ? (
          <div className="mt-1 text-[12px] leading-5 text-[rgba(26,26,26,0.5)]">{description}</div>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </>
  );
}

export function SettingsRow(props: SettingsRowProps) {
  const className = [
    'flex w-full items-center gap-4 rounded-[24px] bg-[rgba(255,248,231,0.72)] px-4 py-3 text-left',
    props.className ?? '',
  ].join(' ');

  if ('href' in props && props.href) {
    const { href, target, rel, label, description, leading, trailing } = props;
    return (
      <a href={href} target={target} rel={rel} className={className}>
        <Content
          label={label}
          description={description}
          leading={leading}
          trailing={trailing}
        />
      </a>
    );
  }

  const buttonProps = props as SettingsRowButtonProps;
  const { type = 'button', label, description, leading, trailing, ...restButtonProps } =
    buttonProps;
  return (
    <button type={type} className={className} {...restButtonProps}>
      <Content
        label={label}
        description={description}
        leading={leading}
        trailing={trailing}
      />
    </button>
  );
}
